import React, { useEffect, useState, useMemo } from 'react';
import { MultiSelectCheckboxTag, Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, SpinnerLoader, Label } from 'lift-ui';
import { HelpCircle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import apiClient from '@lift-admin/utils/apiClient';

interface BrandMultiSelectProps {
  defaultBrands: number[];
  setDefaultBrands: any
}

// Helper function to parse and normalize brand IDs
const normalizeBrandId = (id: any): number => {
  if (id === undefined || id === null) return 0;
  return typeof id === 'number' ? id : parseInt(id, 10);
};

const BrandMultiSelect = ({ defaultBrands, setDefaultBrands }: BrandMultiSelectProps) => {
  const [allBrands, setAllBrands] = useState<any[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Determine the initial mode based on the defaultBrands
  const initialMode = useMemo(() => {
    if (!defaultBrands || !Array.isArray(defaultBrands) || defaultBrands.length === 0) return 'exclude';
    return defaultBrands.some(id => normalizeBrandId(id) > 0) ? 'include' : 'exclude';
  }, []);

  const [mode, setMode] = useState<'exclude' | 'include'>(initialMode);

  const hasMixedMode = useMemo(() => {
    if (!defaultBrands || !Array.isArray(defaultBrands)) return false;
    const hasPositive = defaultBrands.some(id => normalizeBrandId(id) > 0);
    const hasNegative = defaultBrands.some(id => normalizeBrandId(id) < 0);
    return hasPositive && hasNegative;
  }, [defaultBrands]);

  const getAllBrands = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/brand/v1/user/brand/all`);
      const brandsData = res.data.response.map((brand: any) => ({
        value: brand.id,
        label: brand.name,
        id: brand.id,
        name: brand.name
      }));
      setAllBrands(brandsData);
      setLoading(false);
    } catch (error) {
      console.log(error, 'error-getall');
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllBrands();
  }, []);

  useEffect(() => {
    if (!allBrands?.length || !defaultBrands?.length) {
      setSelectedBrands([]);
      return;
    }
    const brandIdToType: Record<string, 'include' | 'exclude'> = {};
    defaultBrands.forEach(id => {
      const normalizedId = normalizeBrandId(id);
      const absId = Math.abs(normalizedId);
      brandIdToType[absId.toString()] = normalizedId > 0 ? 'include' : 'exclude';
    });
    const absIds = defaultBrands.map(id => Math.abs(normalizeBrandId(id)));
    const matchedBrands = allBrands.filter(brand => {
      const brandId = Math.abs(normalizeBrandId(brand.id));
      return absIds.includes(brandId);
    });
    const relevantBrands = matchedBrands.filter(brand => {
      const brandId = Math.abs(normalizeBrandId(brand.id));
      const brandIdKey = brandId.toString();
      return (mode === 'exclude' && brandIdToType[brandIdKey] === 'exclude') ||
             (mode === 'include' && brandIdToType[brandIdKey] === 'include');
    });
    setSelectedBrands(relevantBrands);
  }, [defaultBrands, allBrands, mode]);

  const handleBrandChange = (selectedValues: string[]) => {
    const newValue = selectedValues.map((valueStr) => {
      const brandId = parseInt(valueStr, 10);
      return allBrands.find(brand => Math.abs(normalizeBrandId(brand.id)) === Math.abs(brandId));
    }).filter(Boolean) as any[];

    setSelectedBrands(newValue);

    const newModeIds = newValue.map((brand: any) => {
      const brandId = Math.abs(normalizeBrandId(brand.id));
      return mode === 'exclude' ? -brandId : brandId;
    });

    setDefaultBrands('brands_exclude', newModeIds);
  };

  const handleModeChange = (e: { target: { value: string } }) => {
    setMode(e.target.value as 'exclude' | 'include');
  };

  const selectedValues = useMemo(() => {
    if (!selectedBrands?.length) return [];
    return selectedBrands
      .filter(brand => brand && brand.id !== undefined)
      .map(brand => Math.abs(normalizeBrandId(brand.id)).toString());
  }, [selectedBrands]);

  const multiSelectOptions = useMemo(() => {
    return allBrands.map(brand => ({
      label: brand.label || brand.name || '',
      value: Math.abs(normalizeBrandId(brand.id)).toString()
    }));
  }, [allBrands]);

  const autocompleteLabel = mode === 'exclude'
    ? "Select brands to exclude"
    : "Select brands to include";
  const autocompletePlaceholder = mode === 'exclude'
    ? selectedBrands.length === 0 ? "All brands included" : ""
    : selectedBrands.length === 0 ? "No brands included" : "";

  return (
    <div className="mt-4">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Label className="text-sm font-medium">Brand Access Mode</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-gray-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Exclude mode: Remove access for only specific brands (negative IDs). Include mode: Grant access to only specific brands (positive IDs).</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {hasMixedMode && (
          <p className="text-sm text-red-600 mb-2">
            Warning: Your current configuration has mixed positive and negative IDs.
            This is not recommended. Please choose one mode.
          </p>
        )}

        <RadioGroup
          value={mode}
          onValueChange={(value) => handleModeChange({ target: { value } } as any)}
          className="flex flex-row gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="exclude" id="exclude" />
            <Label htmlFor="exclude" className="flex items-center gap-2 cursor-pointer">
              Exclude specific brands
              <Badge variant="destructive" className="h-5 text-xs">Example</Badge>
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="include" id="include" />
            <Label htmlFor="include" className="flex items-center gap-2 cursor-pointer">
              Include only specific brands
              <Badge variant="default" className="h-5 text-xs bg-green-600">Example</Badge>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <SpinnerLoader className="h-6 w-6" />
        </div>
      ) : (
        <MultiSelectCheckboxTag
          placeholder={autocompletePlaceholder || autocompleteLabel}
          value={selectedValues}
          onChange={handleBrandChange}
          options={multiSelectOptions}
          containerClassName="w-full"
          showSelectAll
        />
      )}

      <p className="text-sm text-gray-600 mt-2">
        {mode === 'exclude'
          ? "If no brands are selected, all brands will have access."
          : "If no brands are selected, no brands will have access."}
      </p>
    </div>
  );
};

export default BrandMultiSelect;
